import { BASIC_POKEMON, ENERGY, STAGE_1, STAGE_2, TRAINER } from '../constants';
import { outcomeFor } from '../GameOver/outcomeMessage';
import { coinFlipGroups } from '../TurnBanner/coinFlipLines';

/** Where a card is, in words: places are named as the server's activity log names them (BoardSnapshot). */
const PLACE_WORDS = { hand: 'hand', deck: 'deck', discard: 'discard pile', prize: 'prizes', active: 'active spot' };
const placeWords = (place) => (place && place.startsWith('bench:') ? 'bench' : PLACE_WORDS[place] || place);
const inPlay = (place) => place === 'active' || (!!place && place.startsWith('bench:'));

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
const list = (names) => (names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/**
 * One move of the game's activity log in words, for the player reading it: a list of sentences ({ text, cards }:
 * the words, and the ids of the cards they're about, which the log shows beside them), the move first and then what it
 * did. An energy type in the words is written {fire} and shown as its symbol, and card and attack names **Machop**,
 * shown bold ("Mewtwo used Psychic on Machop: 40 damage (20, doubled for Weakness).", "Machop was knocked
 * out."). The entry is what the server sent (games/{id}/log): notes about the move, its coin flips, the cards that
 * went from place to place (only counted where the reader can't see them), and how each Pokémon's damage and Special
 * Conditions changed.
 *
 * @param {object} entry
 * @param {object} context
 * @param {number} context.me          the reader's user id: their moves say "You"
 * @param {Map}    context.cardsById   the card catalog
 * @param {object} [context.names]     user id to name, for someone watching (no "You"): moves say whose by name
 * @returns {{ text: string, cards: number[] }[]}
 */
export function describeEntry(entry, { me, cardsById, names = null }) {
  const card = (id) => cardsById.get(id);
  // Card and attack names stand out in the log: written **Machop**, shown bold (LogText).
  const name = (id) => (card(id) ? `**${card(id).get('name')}**` : 'a card');
  const types = (id) => (card(id) ? card(id).get('cardTypes').map(type => type.get('id')).toArray() : []);
  const is = (id, type) => types(id).includes(type);
  // The reader is "you"; the other player is "your opponent", whoever they are. Someone watching reads names.
  const who = (userId) => (names ? names[userId] || 'A player' : userId === me ? 'You' : 'Your opponent');
  // Both players often have the same Pokémon: say whose. Sentences that start with one capitalize it.
  const whose = (userId, cardId) => {
    if (names) {
      return `${who(userId)}'s ${name(cardId)}`;
    }
    return userId === me ? `your ${name(cardId)}` : `your opponent's ${name(cardId)}`;
  };
  const Whose = (userId, cardId) => capitalize(whose(userId, cardId));
  // Whoever didn't make the move: only whether it's the reader matters to the words.
  const otherPlayer = names
    ? Number(Object.keys(names).find(id => Number(id) !== entry.user_id))
    : (entry.user_id === me ? null : me);
  const moveName = (moveId) => {
    const owner = cardsById.find(c => c.get('moves') && c.get('moves').some(move => move.get('id') === moveId));
    return owner ? `**${owner.get('moves').find(move => move.get('id') === moveId).get('name')}**` : 'an attack';
  };

  const notes = entry.notes || [];
  const actor = who(entry.user_id);
  const lines = [];
  // A sentence, with the cards it's about (shown beside it: their pictures, or energy symbols for energy).
  const say = (text, cards = []) => lines.push({ text, cards: cards.filter(id => id !== null && id !== undefined) });
  const played = new Set(notes.filter(note => note.kind === 'trainer').map(note => note.card));
  const knockedOut = notes.filter(note => note.kind === 'knocked_out');
  const attack = notes.find(note => note.kind === 'attack');

  notes.forEach((note) => {
    switch (note.kind) {
      case 'deck':
        say(`${actor} chose a deck.`);
        break;
      case 'coin_call': {
        const flip = (entry.flips || [])[0];
        const chooses = who(note.chooser_id) === 'You' ? 'you choose' : `${who(note.chooser_id)} chooses`;
        const landed = flip ? ` It came up ${flip.heads ? 'heads' : 'tails'}, so ${chooses} who goes first.` : '';
        say(`${actor} called ${note.heads ? 'heads' : 'tails'}.${landed}`);
        break;
      }
      case 'first_player':
        say(`${who(note.user_id) === 'You' ? 'You go' : `${who(note.user_id)} goes`} first.`);
        break;
      case 'ready':
        say(`${actor} got ready.`);
        break;
      case 'game_started':
        say('The game started.');
        break;
      case 'attack':
        say(describeAttack(note, {
          attacker: Whose(entry.user_id, note.attacker),
          defender: whose(otherPlayer, note.defender),
          type: card(note.attacker) ? card(note.attacker).get('energy_id') : null,
          moveName,
        }), [note.attacker, note.defender]);
        // What the attack left for the next turn, in the words of the card's badge.
        if (note.hindrance) {
          say(`${Whose(otherPlayer, note.defender)}: ${lowerFirst(note.hindrance)} next turn.`, [note.defender]);
        }
        if (note.protection) {
          say(`${Whose(entry.user_id, note.attacker)}: ${lowerFirst(note.protection)} next turn.`, [note.attacker]);
        }
        break;
      case 'knocked_out':
        say(`${Whose(note.owner, note.card)} was knocked out.`, [note.card]);
        break;
      case 'trainer':
        say(`${actor} played ${name(note.card)}.`, [note.card]);
        break;
      case 'power':
        say(`${actor} used ${name(note.card)}'s ${moveName(note.move)}.`, [note.card]);
        break;
      case 'retreat_failed':
        say(`${actor} tried to retreat, but the coin said no (Confused).`);
        break;
      case 'pass':
        say(`${actor} ended the turn.`);
        break;
      case 'arranged':
        // Bench spots are numbered from 1 for people.
        say(note.swapped
          ? `${actor} swapped ${name(note.card)} and ${name(note.swapped)} on the bench.`
          : `${actor} moved ${name(note.card)} to bench spot ${note.to + 1}.`, [note.card, note.swapped]);
        break;
      default:
        break;
    }
  });

  // The coin call says its own flip; the others are the attack's or a card's.
  const flips = notes.some(note => note.kind === 'coin_call') ? (entry.flips || []).slice(1) : (entry.flips || []);
  // One line for each thing the coins were for: an attack's coin and a Pokémon waking up are separate.
  coinFlipGroups(flips).forEach(({ label, flips: group }) => {
    say(`Coin${group.length === 1 ? '' : 's'}: ${group.map(flip => (flip.heads ? 'heads' : 'tails')).join(', ')} (${label}).`);
  });

  (entry.cards || []).forEach((move) => {
    const line = describeCards(move, { who, whose, Whose, name, is, played, knockedOut });
    if (line) {
      say(line, move.cards || []);
    }
  });

  // Poison between turns, said on its own line: a Pokémon's damage is added up over the whole move.
  const poisoned = notes.filter(note => note.kind === 'poison');
  (entry.pokemon || []).forEach((change) => {
    // What the attack and poison did is said by them; any more is said here.
    const byAttack = attack && attack.dealt && change.place === 'active' && change.owner !== entry.user_id ? attack.dealt : 0;
    const byPoison = change.place === 'active'
      ? poisoned.filter(note => note.owner === change.owner).reduce((sum, note) => sum + note.damage, 0)
      : 0;
    const more = change.damage > 0 ? change.damage - byAttack - byPoison : 0;
    const pokemon = Whose(change.owner, change.card);
    if (more > 0) {
      say(`${pokemon} took ${more} damage.`, [change.card]);
    }
    if (change.damage < 0) {
      say(`${pokemon} was healed ${-change.damage} damage.`, [change.card]);
    }
    change.gained.forEach(condition => say(`${pokemon} is now ${condition}.`, [change.card]));
    change.lost.forEach(condition => say(`${pokemon} is no longer ${condition}.`, [change.card]));
  });
  poisoned.forEach(note => say(`Between turns, poison did ${note.damage} damage to ${whose(note.owner, note.card)}.`, [note.card]));

  // The game ending is the last thing the move did: after the prize taken or the knockout that ended it.
  const over = notes.find(note => note.kind === 'game_over');
  if (over && names) {
    say(`${who(over.winner)} won the game.`);
  } else if (over) {
    const { title, detail } = outcomeFor(over.winner, over.reason, me);
    say(`**${title}!** ${detail}`);
  }

  return lines;
}

/** An energy type in a sentence, shown as its symbol (LogText): "{fire}". */
export const energyToken = (type) => (type ? `{${type}} ` : '');

function describeAttack(note, { attacker, defender, type, moveName }) {
  const move = moveName(note.move);
  if (note.outcome === 'failed') {
    // Older entries don't say which attack made it fail; Sand-attack was the only one then.
    return `${attacker}'s ${move} did nothing (${note.hindered_by ? moveName(note.hindered_by) : 'Sand-attack'}).`;
  }
  if (note.outcome === 'confused') {
    return `${attacker} tried ${move}, but hurt itself in its confusion.`;
  }
  const copied = note.copied ? `, copying ${moveName(note.copied)}` : '';
  if (note.dealt === undefined) {
    return `${attacker} used ${move} on ${defender}${copied}.`;
  }
  const why = [];
  if (note.weakness) {
    why.push(`doubled for ${energyToken(type)}Weakness`);
  }
  if (note.resistance) {
    why.push(`lowered by ${energyToken(type)}Resistance`);
  }
  if (note.plus_power) {
    why.push(`+${note.plus_power} PlusPower`);
  }
  if (note.defender_trainer) {
    why.push(`−${note.defender_trainer} Defender`);
  }
  if (note.protected) {
    why.push('blocked');
  }
  if (note.reduced) {
    why.push(`−${note.reduced.by} ${moveName(note.reduced.move)}`);
  }
  const workedOut = why.length ? ` (${note.base}, ${why.join(', ')})` : '';
  return `${attacker} used ${move} on ${defender}${copied}: ${note.dealt} damage${workedOut}.`;
}

function describeCards(move, { who, whose, Whose, name, is, played, knockedOut }) {
  const { owner, from, to } = move;
  const owners = who(owner);
  const ids = move.cards || [];
  const count = move.cards ? move.cards.length : move.count;
  const named = move.cards ? list(ids.map(name)) : null;
  const some = (word) => named || plural(count, word);

  // Said elsewhere: a played Trainer, and the cards of a knocked-out Pokémon.
  if (to === 'discard' && from === 'hand' && ids.length && ids.every(id => played.has(id))) {
    return null;
  }
  if (to === 'discard' && inPlay(from) && knockedOut.some(note => note.owner === owner && note.place === from)) {
    return null;
  }

  if (from === 'deck' && to === 'hand') {
    return `${owners} drew ${some('card')}.`;
  }
  if (from === 'deck' && to === 'prize') {
    return `${owners} set out ${plural(count, 'prize card')}.`;
  }
  if (from === 'prize' && to === 'hand') {
    return `${owners} took ${named ? `a prize card: ${named}` : count === 1 ? 'a prize card' : plural(count, 'prize card')}.`;
  }
  // Onto a Pokémon: the server says which (onto), as it was before the move.
  // The player is the sentence's subject, and only plays onto their own Pokémon: "your Machop", or just "Machop".
  const onto = move.onto ? (owners === 'You' ? whose(owner, move.onto) : name(move.onto)) : `the Pokémon in the ${placeWords(to)}`;
  if (from === 'hand' && inPlay(to) && named) {
    const first = ids[0];
    if (is(first, ENERGY) || is(first, TRAINER)) {
      return `${owners} attached ${named} to ${onto}.`;
    }
    if (is(first, STAGE_1) || is(first, STAGE_2)) {
      return `${owners} evolved ${onto} into ${named}.`;
    }
    if (is(first, BASIC_POKEMON)) {
      return to === 'active' ? `${owners} put ${named} in the active spot.` : `${owners} put ${named} on the bench.`;
    }
  }
  // A Pokémon changing places takes its energy, and what it evolved from, with it: name just the Pokémon on top.
  const top = [STAGE_2, STAGE_1, BASIC_POKEMON].map(stage => ids.find(id => is(id, stage))).find(id => id !== undefined);
  const pokemon = top !== undefined ? Whose(owner, top) : named;
  if (from && from.startsWith('bench:') && to === 'active') {
    return `${pokemon} moved up to the active spot.`;
  }
  if (from === 'active' && to && to.startsWith('bench:')) {
    return `${pokemon} went to the bench.`;
  }
  if (inPlay(from) && to === 'discard') {
    return `${owners} discarded ${named} from the ${placeWords(from)}.`;
  }
  if (from === 'hand' && to === 'discard') {
    return `${owners} discarded ${some('card')}.`;
  }
  if (to === 'deck') {
    return `${owners} shuffled ${some('card')} into the deck.`;
  }
  return `${owners} moved ${some('card')} from the ${placeWords(from)} to the ${placeWords(to)}.`;
}
